import { CSSProperties } from 'preact/compat'

export type Image = {
  height: number
  width: number
  url: string
}

export type FieldEntry = {
  code: string
  value: string | number | SelectableFieldValue | TextareaFieldValue
}

export enum FieldLayout {
  Compact = 'COMPACT',
  Normal = 'NORMAL',
}

export enum TriggerEvent {
  FieldChange = 'FieldChange',
  PageComplete = 'PageComplete',
  Complete = 'Complete',
  Loaded = 'Loaded',
  HeightChange = 'HeightChange',
  Close = 'OnClose',
  CaptureWindow = 'CaptureWindow',
}

export type Message = {
  name: TriggerEvent
  data?: any
}

export type NextFields = {
  nodes: any[]
  startIndex: number
  totalCount: number
  isLastPage: boolean
}

export enum Locale {
  En = 'EN',
  Tw = 'TW',
  ZhCn = 'ZH_CN',
}

export type RatingDescription = {
  hate: string
  dislike: string
  neutral: string
  like: string
  love: string
}

export type ScreenshotDescription = {
  inProgress: string
  captured: string
  screenshot: string
}

export type Wordings = {
  branding: string
  nextButton: string
  submitButton: string
  otherChoicePlaceholder: string
  ratingDescription: RatingDescription
  screenshotDescription: ScreenshotDescription
}

export type FormSetting = {
  backNavigation: boolean
  showProgressBar: boolean
  successMessage: string
  colorScheme?: ColorScheme
  buttonStyle: ButtonStyle
  brandingUrl: string | null
  welcome?: FormWelcomeSetting
  textStyle: TextStyle
  localeSetting?: {
    locale: Locale
    wordings: Wordings
  }
}

export type FormWelcomeSetting = {
  title: string
  content: string
  buttonText: string
}

export type ColorScheme = {
  accent: string
  background: string
}

export enum ButtonVariant {
  FILLED = 'FILLED',
  OUTLINE = 'OUTLINE',
}

export type ButtonStyle = {
  variant: ButtonVariant
  borderRadius: number
}

export enum PARENT_WINDOW_SIZE {
  SM = 'SM',
  MD = 'MD',
}

export enum CampaignType {
  FEEDBACK = 'FEEDBACK',
  SURVEY = 'SURVEY',
  REPLY = 'REPLY',
  NOTIFICATION = 'NOTIFICATION',
}

export enum PublishedEntryMode {
  NORMAL = 'NORMAL',
  PREVIEW = 'PREVIEW',
}

export type Page = {
  nodes: Field[]
  startIndex?: number
  totalCount?: number
  isLastPage: boolean
}

export type SurveyFieldProps = {
  pages: Array<Page>
}

export type FeedbackFieldProps = {
  ratingField: RatingField
  textareaField: TextArea | null
  onRatingFieldChange: (data: any) => void
  onTextAreaFieldChange?: (data: any) => void
  onSubmit: () => void
  onFieldHeightChange?: (height: number) => void
  style?: CSSProperties
}

export enum ConditionGroupOperator {
  And = 'AND',
  Or = 'OR',
}

export enum ConditionOperator {
  After = 'AFTER',
  Before = 'BEFORE',
  /** @deprecated Use DATE_BETWEEN instead */
  Between = 'BETWEEN',
  Contains = 'CONTAINS',
  DateBetween = 'DATE_BETWEEN',
  DateEq = 'DATE_EQ',
  DateNe = 'DATE_NE',
  DateNotBetween = 'DATE_NOT_BETWEEN',
  /** NPS反对者 一元运算 */
  Detractor = 'DETRACTOR',
  /** == */
  Eq = 'EQ',
  /** 不包含/没选择 */
  Exclude = 'EXCLUDE',
  /** > */
  Gt = 'GT',
  /** >= */
  Gte = 'GTE',
  /** 包含/选择了 */
  Include = 'INCLUDE',
  IsNotNull = 'IS_NOT_NULL',
  IsNull = 'IS_NULL',
  /** < */
  Lt = 'LT',
  /** <= */
  Lte = 'LTE',
  /** != */
  Ne = 'NE',
  /** 消极情绪 */
  Negative = 'NEGATIVE',
  /** 中立情绪 */
  Neutral = 'NEUTRAL',
  /** @deprecated Use DATE_NOT_BETWEEN instead */
  NotBetween = 'NOT_BETWEEN',
  NotContains = 'NOT_CONTAINS',
  NumberBetween = 'NUMBER_BETWEEN',
  /** NPS中立者 一元运算 */
  Passive = 'PASSIVE',
  /** 积极情绪 */
  Positive = 'POSITIVE',
  /** NPS支持者 一元运算 */
  Promoter = 'PROMOTER',
  /** 通配符 */
  Wildcard = 'WILDCARD',
}

export enum ConditionNodeType {
  Constant = 'CONSTANT',
  CookieValue = 'COOKIE_VALUE',
  CustomerAttrs = 'CUSTOMER_ATTRS',
  CustomerCustomizedAttrs = 'CUSTOMER_CUSTOMIZED_ATTRS',
  CustomerTags = 'CUSTOMER_TAGS',
  CustomizedDate = 'CUSTOMIZED_DATE',
  CustomizedPeriod = 'CUSTOMIZED_PERIOD',
  CustomizedProps = 'CUSTOMIZED_PROPS',
  Event = 'EVENT',
  Future = 'FUTURE',
  Literal = 'LITERAL',
  OrdinaryVariable = 'ORDINARY_VARIABLE',
  Past = 'PAST',
  Period = 'PERIOD',
  SentimentVariable = 'SENTIMENT_VARIABLE',
  TimePeriod = 'TIME_PERIOD',
}

type ConditionNode = {
  label?: string
  type?: ConditionNodeType
  value?: any
}

export type Condition = {
  firstNode: ConditionNode
  operator: ConditionOperator
  secondNode?: ConditionNode
}

export type ConditionGroup = {
  conditions: Condition[]
  operator: ConditionGroupOperator
}

export type FieldShowRule = {
  conditionGroup?: ConditionGroup
  key?: string
  targets: string[]
}

export enum FieldJumpRuleTargetType {
  NextField = 'NEXT_FIELD',
  SpecificField = 'SPECIFIC_FIELD',
  Ending = 'ENDING',
}

export type FieldJumpRule = {
  subRules?: FieldJumpRule[]
  targetType: FieldJumpRuleTargetType
  conditionGroup?: ConditionGroup
}

export type BaseField = {
  code: string
  label: string
  type: string
  description?: string
  validators?: FieldValidator[]
  labelHidden: boolean
  hidden: boolean
  showRules?: FieldShowRule[]
  jumpRule?: FieldJumpRule
  parentCode?: string
  page?: number
  image?: Image
}

export type FieldValidator = {
  type: FieldValidatorType
}

export enum FieldValidatorType {
  Presence = 'PRESENCE',
}

export enum RATING_TYPES {
  EMOJI = 'emoji',
  STAR = 'star',
  HEART = 'heart',
  NUMBER = 'number',
}

export type RatingField = BaseField & {
  ratingMax: number
  ratingType: RATING_TYPES
  ratingMinDescription: string | null
  ratingMaxDescription: string | null
  ratingDescriptions: string[]
  layout: FieldLayout
}

export type NpsField = BaseField & {
  ratingMax: number
  ratingMinDescription: string | null
  ratingMaxDescription: string | null
}

export type EndingField = BaseField & {
  content: string
  autoCloseEnabled?: boolean
  autoCloseDuration?: number
}

export type WelcomeField = BaseField & {
  content: string
  buttonText: string
}

export type TextArea = BaseField & {
  placeholder?: string
  screenshotsEnabled: boolean
}

export type TextField = BaseField & {
  placeholder?: string
}

export type Choice = {
  label: string
  type: ChoiceType
  value: string
}

export enum ChoiceStyle {
  TAG = 'TAG',
  LIST = 'LIST',
}

export enum ChoicesSize {
  Large = 'LARGE',
  Small = 'SMALL',
}

export type CheckboxSelectField = BaseField & {
  choicesStyle: ChoiceStyle
  choicesSize: ChoicesSize
  choicesRandom: boolean
  choices: Choice[]
  rowChoiceCount?: number
  minChoices?: number | null
  maxChoices?: number | null
}

export type RadioSelectField = BaseField & {
  choicesRandom: boolean
  choicesStyle: ChoiceStyle
  choicesSize: ChoicesSize
  rowChoiceCount?: number
  choices: Choice[]
}

export type CsatField = BaseField & {
  ratingDescriptions: string[]
}

export type CesField = BaseField & {
  ratingMax: number
  ratingMinDescription: string | null
  ratingMaxDescription: string | null
}

export enum ButtonAlign {
  LEFT = 'LEFT',
  CENTER = 'CENTER',
  RIGHT = 'RIGHT',
}

export type CtaField = BaseField & {
  choices: Choice[]
  primaryButtonLink?: string
  secondaryButtonLink?: string
  buttonAlign?: ButtonAlign
}

export type DescriptionField = BaseField

export type Field =
  | CheckboxSelectField
  | RadioSelectField
  | RatingField
  | TextField
  | TextArea
  | NpsField
  | EndingField
  | WelcomeField
  | CsatField
  | CesField
  | DescriptionField
  | CtaField

export const OtherChoiceValue = 'choiceOther'

export enum ChoiceType {
  TextChoice = 'TextChoice',
  OtherChoice = 'OtherChoice',
}

export type SelectableFieldValue = {
  value?: string[]
  other?: string
}

export type TextareaFieldValue = {
  text?: string
  screenshots?: string[]
}

export type FeedbackFieldRefType = { dom: HTMLDivElement; fadeIn: () => void }

export enum FormFieldType {
  CesField = 'CesField',
  CheckBox = 'CheckBox',
  CsatField = 'CsatField',
  EndingField = 'EndingField',
  NpsField = 'NpsField',
  RadioButton = 'RadioButton',
  RatingField = 'RatingField',
  TextArea = 'TextArea',
  TextField = 'TextField',
  WelcomeField = 'WelcomeField',
  DescriptionField = 'DescriptionField',
  CtaField = 'CtaField',
}

export type Platform = 'mobile' | 'desktop'

export type TextStyle = {
  align: TextAlign
}

export enum TextAlign {
  LEFT = 'LEFT',
  CENTER = 'CENTER',
}

export interface GraphQLErrorExtensions {
  [attributeName: string]: unknown
}

export type GraphQLError = Error & {
  extensions?: GraphQLErrorExtensions
}
